/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useEffect, useState } from "react";
import { sectionCodeOutcomeDesirability, sectionCodeRelativeImportance } from "../dictionaries/CodeSystemLookup";
import StringEntry from "../datatypeComponents/StringEntry";
import ReferenceEntry, { DisplayFromFHIRReference, displayStringFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import CodeableConceptEntry, { simpleDisplayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import UriEntry, { DisplayFromFHIRUri } from "../datatypeComponents/UriEntry";
import DatatypeSelector from "../datatypeComponents/DatatypeSelector";
import { displayStringFromFHIRBoolean } from "../datatypeComponents/BooleanEntry";
import { displayStringFromFHIRQuantity } from "../datatypeComponents/QuantityEntry";
import { useDispatch, useSelector } from "react-redux";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import UnsignedIntEntry from "../datatypeComponents/UnsignedIntEntry";
import ExpandToAddOrEdit from "./ExpandToAddOrEdit";

const DisplayFromFHIRRating = ({ rating }) => {
    if (rating) {
        if (Array.isArray(rating) || typeof rating !== "object") {
            return <>[ERROR: Rating data not interpretable.]</>;
        }
        let ratingType = simpleDisplayStringFromFHIRCodeableConcept(rating.type) || "[Untyped]";
        let ratingClassifiers = "";
        if (Array.isArray(rating.classifier) && rating.classifier.length > 0) {
            ratingClassifiers = rating.classifier.map((classifier) => { return simpleDisplayStringFromFHIRCodeableConcept(classifier) }).join('; ');
        }
        let ratingQuantity = "";
        if (rating.quantity) {
            ratingQuantity = displayStringFromFHIRQuantity(rating.quantity) || "";
        }
        let ratingAuthor = "";
        if (Array.isArray(rating.author) && rating.author.length > 0) {
            ratingAuthor = rating.author.map((author) => { return displayStringFromFHIRReference(author) }).join("; ");
        }
        let ratingFreeToShare = displayStringFromFHIRBoolean(rating.freeToShare) || null;
        return <div>
            <p><b>{ratingType} ratings:</b></p>
            <div style={{ marginLeft: "24px" }}>
                {ratingClassifiers && <p>{ratingClassifiers}</p>}
                {ratingQuantity && <p>{ratingQuantity}</p>}
                {(!ratingClassifiers && !ratingQuantity) && <p>[No ratings.]</p>}
                {ratingAuthor && <p>Rated by: {ratingAuthor}</p>}
                {ratingFreeToShare === 'True' && <p>Free to share.</p>}
                {ratingFreeToShare === 'False' && <p>NOT free to share.</p>}
                {(Array.isArray(rating.component) && rating.component.length > 0) &&
                    rating.component.map((component, componentIndex) => {
                        return <div key={componentIndex}>
                            <DisplayFromFHIRRating rating={component} />
                        </div>
                    })}
            </div>
        </div>
    } else {
        return <></>
    }
}

const OutcomeImportanceContentEntry = memo(({ elementName, fieldLabel, startingValue,
    dataEntryStyle, startCollapsed, foi }) => {

    const dispatch = useDispatch();
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
    let initialResourceJson = newResourceDetail.resourceJson;
    let resourceJson = newResourceDetail.revisedResourceJson;

    let contentType = "";
    if (dataEntryStyle === "Outcome Desirability") {
        contentType = sectionCodeOutcomeDesirability;
    } else if (dataEntryStyle === "Relative Importance") {
        contentType = sectionCodeRelativeImportance;
    }
    let startingOutcomeImportanceContent = {
        "summary": "", "type": contentType,
        "classifier": [], classifierInstance: "", "quantity": "", "quantityValue": 0,
        "author": [], "path": [], "relatesTo": [], "freeToShare": true,
        "component": []
    };

    if (!startingValue) {
        startingOutcomeImportanceContent = "";
    } else {
        if (startingValue.extension) { startingOutcomeImportanceContent.extension = startingValue.extension; }
        if (startingValue.summary) { startingOutcomeImportanceContent.summary = startingValue.summary; }
        if (startingValue.type) { startingOutcomeImportanceContent.type = startingValue.type; }
        if (startingValue.classifier) {
            startingOutcomeImportanceContent.classifier = startingValue.classifier;
            startingOutcomeImportanceContent.classifierInstance = startingValue.classifier[0];
        }
        if (startingValue.quantity) {
            startingOutcomeImportanceContent.quantityValue = startingValue.quantity.value;
        }
        if (startingValue.author) { startingOutcomeImportanceContent.author = startingValue.author; }
        if (startingValue.relatesTo) { startingOutcomeImportanceContent.relatesTo = startingValue.relatesTo; }
        if (typeof startingValue.freeToShare === "boolean") { startingOutcomeImportanceContent.freeToShare = startingValue.freeToShare; }
    }
    const [outcomeImportanceContentState, setOutcomeImportanceContentState] = useState(JSON.parse(JSON.stringify(startingOutcomeImportanceContent || {})));
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        if (outcomeImportanceContentState.type) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            if (elementName === "content[0]") {
                previousInitialValue = initialResourceJson.content[0].classifier?.[0];
                previousFinalValue = resourceJson.content[0].classifier?.[0];
                resourceJson.content[0].classifier = [outcomeImportanceContentState.classifierInstance];
                if (outcomeImportanceContentState.classifierInstance !== undefined &&
                    JSON.stringify(previousFinalValue) !== JSON.stringify([outcomeImportanceContentState.classifierInstance]) &&
                    !(!previousFinalValue && !outcomeImportanceContentState.classifierInstance)) {
                    console.log("resourceUpdate for ArtifactAssessment.content[0].classifier[0]");
                    dispatch(resourceUpdated(newResourceDetail));
                    updateChangeReport(dispatch, resourceChanges, foi,
                        "CodeableConcept",
                        "content[0].classifier[0]",
                        previousInitialValue,
                        outcomeImportanceContentState.classifierInstance);
                }
            }
        }
    }, [outcomeImportanceContentState.classifierInstance]);

    useEffect(() => {
        if (outcomeImportanceContentState.type) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            if (elementName === "content[1]") {
                previousInitialValue = initialResourceJson.content[1].quantity;
                previousFinalValue = resourceJson.content[1].quantity;
                resourceJson.content[1].quantity = {
                    "value": outcomeImportanceContentState.quantityValue,
                    "unit": "%",
                    "system": "http://unitsofmeasure.org",
                    "code": "%"
                };
                if (outcomeImportanceContentState.quantityValue !== undefined &&
                    JSON.stringify(previousFinalValue) !== JSON.stringify({
                        "value": outcomeImportanceContentState.quantityValue,
                        "unit": "%",
                        "system": "http://unitsofmeasure.org",
                        "code": "%"
                    }) &&
                    !(!previousFinalValue && !outcomeImportanceContentState.quantityValue)) {
                    console.log("resourceUpdate for ArtifactAssessment.content[1].quantity");
                    dispatch(resourceUpdated(newResourceDetail));
                    updateChangeReport(dispatch, resourceChanges, foi,
                        "Quantity",
                        "content[1].quantity",
                        previousInitialValue,
                        {
                            "value": outcomeImportanceContentState.quantityValue,
                            "unit": "%",
                            "system": "http://unitsofmeasure.org",
                            "code": "%"
                        });
                }
            }
        }
    }, [outcomeImportanceContentState.quantityValue]);

    if (startCollapsedState) {
        return <>
            <div>
                <b>{fieldLabel}: </b>
                {startingValue && <DisplayFromFHIRRating rating={startingOutcomeImportanceContent} />}
                &nbsp;&nbsp;
                <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
            </div>
        </>
    } else if (dataEntryStyle === "Outcome Desirability") {
        return <>
            <p style={{ marginBottom: "0px" }}><b>Outcome Desirability: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='classifier' fieldLabel='Outcome Desirability'
                    startingValue={outcomeImportanceContentState.classifierInstance || null}
                    setResourceState={setOutcomeImportanceContentState}
                    codeableConceptLevelValueSet={[ //Outcome Desirability
                        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "desirable", display: "Desirable" }] },
                        { "coding": [{ system: "https://fevir.net/resources/CodeSystem/179423", code: "undesirable", display: "Undesirable" }] }
                    ]} />
            </div>
        </>
    } else if (dataEntryStyle === "Relative Importance") {
        return <>
            <p style={{ marginBottom: "0px" }}><b>Relative Importance: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <UnsignedIntEntry elementName='quantityValue' fieldLabel='Relative Importance (%)'
                    startingValue={outcomeImportanceContentState.quantityValue || 0}
                    setResourceState={setOutcomeImportanceContentState} />
            </div>
        </>
    } else {
        return <>
            <p>Something is not right.</p>
        </>
    }
})

const OutcomeImportanceEdit = ({ foi }) => {

    const dispatch = useDispatch();
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
    let initialResourceJson = newResourceDetail.resourceJson;
    let resourceJson = newResourceDetail.revisedResourceJson;

    const [editResourceState, setEditResourceState] = useState({});
    const [resourceLoaded, setResourceLoaded] = useState(false);
    const [artifactReferenceEditState, setArtifactReferenceEditState] = useState(false);
    const [artifactReferenceDatatypeState, setArtifactReferenceDatatypeState] = useState('none');
    const [contentArrayState, setContentArrayState] = useState({});

    useEffect(() => {
        if (resourceJson && !resourceLoaded) {
            let startingArtifactReferenceDatatype = 'none';
            if (resourceJson.artifactReference) { startingArtifactReferenceDatatype = 'Reference'; }
            if (resourceJson.artifactCanonical) { startingArtifactReferenceDatatype = 'canonical'; }
            if (resourceJson.artifactUri) { startingArtifactReferenceDatatype = 'uri'; }
            setArtifactReferenceDatatypeState(startingArtifactReferenceDatatype);
            if (!resourceJson.content || !Array.isArray(resourceJson.content) || resourceJson.content.length === 0) {
                resourceJson.content = [
                    {
                        "type": sectionCodeOutcomeDesirability
                    }
                ]
            }
            if (resourceJson.content.length === 1) {
                resourceJson.content.push({
                    "type": sectionCodeRelativeImportance
                })
            }

            setContentArrayState({
                "content[0]": resourceJson.content[0],
                "content[1]": resourceJson.content[1]
            });
            setEditResourceState({
                title: resourceJson.title,
                artifactReference: resourceJson.artifactReference,
                artifactCanonical: resourceJson.artifactCanonical,
                artifactUri: resourceJson.artifactUri,
                relatesTo: resourceJson.relatesTo,
                content: resourceJson.content
            });
            setResourceLoaded(true);
        }
    }, [resourceLoaded])

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = editResourceState.title;
            newResourceDetail.displayTitle = editResourceState.title;
            if (editResourceState.title !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.title)) {
                console.log("resourceUpdate for ArtifactAssessment.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "string",
                    "title",
                    previousInitialValue,
                    editResourceState.title);
            }
        }
    }, [editResourceState.title]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.artifactReference;
            previousFinalValue = newResourceDetail.revisedResourceJson.artifactReference;
            newResourceDetail.revisedResourceJson.artifactReference = editResourceState.artifactReference;
            if (editResourceState.artifactReference !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.artifactReference) &&
                !(!previousFinalValue && !editResourceState.artifactReference)) {
                console.log("resourceUpdate for ArtifactAssessment.artifactReference");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "Reference",
                    "artifactReference",
                    previousInitialValue,
                    editResourceState.artifactReference);
            }
        }
    }, [editResourceState.artifactReference]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.artifactCanonical;
            previousFinalValue = newResourceDetail.revisedResourceJson.artifactCanonical;
            newResourceDetail.revisedResourceJson.artifactCanonical = editResourceState.artifactCanonical;
            if (editResourceState.artifactCanonical !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.artifactCanonical) &&
                !(!previousFinalValue && !editResourceState.artifactCanonical)) {
                console.log("resourceUpdate for ArtifactAssessment.artifactCanonical");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "canonical",
                    "artifactCanonical",
                    previousInitialValue,
                    editResourceState.artifactCanonical);
            }
        }
    }, [editResourceState.artifactCanonical]);

    useEffect(() => {
        if (editResourceState) {
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = initialResourceJson.artifactUri;
            previousFinalValue = newResourceDetail.revisedResourceJson.artifactUri;
            newResourceDetail.revisedResourceJson.artifactUri = editResourceState.artifactUri;
            if (editResourceState.artifactUri !== undefined &&
                JSON.stringify(previousFinalValue) !== JSON.stringify(editResourceState.artifactUri) &&
                !(!previousFinalValue && !editResourceState.artifactUri)) {
                console.log("resourceUpdate for ArtifactAssessment.artifactUri");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, foi,
                    "uri",
                    "artifactUri",
                    previousInitialValue,
                    editResourceState.artifactUri);
            }
        }
    }, [editResourceState.artifactUri]);

    useEffect((() => {
        if (artifactReferenceDatatypeState === 'uri') {
            setEditResourceState(prevState => { return { ...prevState, 'artifactReference': null, 'artifactCanonical': null } })
        }
        if (artifactReferenceDatatypeState === 'Reference') {
            setEditResourceState(prevState => { return { ...prevState, 'artifactUri': null, 'artifactCanonical': null } })
        }
        if (artifactReferenceDatatypeState === 'canonical') {
            setEditResourceState(prevState => { return { ...prevState, 'artifactReference': null, 'artifactUri': null } })
        }
    }), [artifactReferenceDatatypeState]);

    return <div style={{ marginTop: "12px" }}>
        <h3 id="summary">Summary</h3>
        <div style={{ marginLeft: "24px" }}>
            <StringEntry elementName='title' fieldLabel='Title'
                startingValue={editResourceState.title}
                setResourceState={setEditResourceState} />
        </div>
        <div>
            <div><b>The Outcome Being Rated: </b>
                <DisplayFromFHIRReference reference={editResourceState.artifactReference} />
                <DisplayFromFHIRUri uri={editResourceState.artifactUri || editResourceState.artifactCanonical} />
                <span className={"unselectable"} style={{ marginLeft: "24px", cursor: "pointer" }}
                    onClick={() => { setArtifactReferenceEditState(!artifactReferenceEditState) }}>
                    <i>Change the Outcome (EvidenceVariable Resource) Being Rated</i>&nbsp;&nbsp;
                    {artifactReferenceEditState ? <>▼</> : <>►</>}
                </span>
            </div>
            {artifactReferenceEditState &&
                <div style={{ whiteSpace: "pre-wrap", paddingLeft: "24px" }}>
                    <DatatypeSelector elementXName='artifact[x]' allowedDatatypes={['Reference', 'canonical', 'uri']}
                        datatypeState={artifactReferenceDatatypeState} setDatatypeState={setArtifactReferenceDatatypeState} />
                    {(artifactReferenceDatatypeState === 'uri') &&
                        <UriEntry elementName='artifactUri'
                            fieldLabel='The Outcome Being Rated (by URL)'
                            startingValue={editResourceState.artifactUri || null}
                            setResourceState={setEditResourceState} />}
                    {(artifactReferenceDatatypeState === 'Reference') &&
                        <ReferenceEntry elementName='artifactReference'
                            fieldLabel='The Outcome Being Rated' startingResourceType="EvidenceVariable"
                            startingValue={editResourceState.artifactReference || null}
                            setResourceState={setEditResourceState} />}
                    {(artifactReferenceDatatypeState === 'canonical') &&
                        <UriEntry elementName='artifactCanonical'
                            fieldLabel='The Outcome Being Rated (by Canonical URI)'
                            startingValue={editResourceState.artifactCanonical || null}
                            setResourceState={setEditResourceState} />}
                    <br />
                </div>
            }
            <h3 id="outcome-desirability">Outcome Desirability</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[0]"] ?
                    <OutcomeImportanceContentEntry elementName='content[0]'
                        fieldLabel='Outcome Desirability' foi={foi}
                        startingValue={contentArrayState["content[0]"] || null}
                        dataEntryStyle='Outcome Desirability' startCollapsed />
                    :
                    <p>Loading ...</p>}
                <br />
                <br />
            </div>
            <br />
        </div>
        <div>
            <h3 id="relative-importance">Relative Importance</h3>
            <div style={{ marginLeft: "24px" }}>
                {contentArrayState["content[1]"] ?
                    <OutcomeImportanceContentEntry elementName='content[1]' fieldLabel='Relative Importance'
                        startingValue={contentArrayState["content[1]"] || null} foi={foi}
                        dataEntryStyle='Relative Importance' startCollapsed />
                    :
                    <p>Loading ...</p>}
            </div>
            <br />
        </div>
    </div>
};

export default OutcomeImportanceEdit;